import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { parseInternationalPhoneNumber } from "../../../../utils/internationalPhoneNumber";

import type { components } from "./schema";

export type SwiftDestinationFormType =
  components["schemas"]["SwiftDestinationFormType"];

export type SwiftDestinationFields =
  components["schemas"]["SwiftDestinationFields"];

const DEFAULT_POSTAL_CODE = "00000";
export const POSTAL_CODE_NOT_APPLICABLE_COUNTRIES = new Set([
  "AE",
  "AG",
  "AO",
  "AW",
  "BF",
  "BI",
  "BJ",
  "BS",
  "BW",
  "BZ",
  "CC",
  "CF",
  "CI",
  "CK",
  "CM",
  "CW",
  "CX",
  "DJ",
  "DM",
  "ER",
  "FJ",
  "GA",
  "GD",
  "GM",
  "GQ",
  "GW",
  "GY",
  "HK",
  "JM",
  "KI",
  "KN",
  "LC",
  "LR",
  "ML",
  "MO",
  "MR",
  "NR",
  "NU",
  "QA",
  "RW",
  "SB",
  "SL",
  "SR",
  "ST",
  "TD",
  "TG",
  "TK",
  "TL",
  "TO",
  "TV",
  "UG",
  "VC",
  "VU",
  "WS",
  "YE",
]);
const POSTAL_CODE_OPTIONAL_COUNTRIES = new Set([
  ...POSTAL_CODE_NOT_APPLICABLE_COUNTRIES,
  "AD",
  "AI",
  "AL",
  "AM",
  "AQ",
  "AR",
  "AS",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BH",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BT",
  "BV",
  "CH",
  "CL",
  "CN",
  "CO",
  "CR",
  "CV",
  "DO",
  "DZ",
  "EC",
  "EG",
  "EH",
  "ET",
  "FK",
  "FM",
  "FO",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GN",
  "GP",
  "GS",
  "GT",
  "GU",
  "HM",
  "HN",
  "ID",
  "IL",
  "IM",
  "IS",
  "JE",
  "JO",
  "KE",
  "KG",
  "KH",
  "KM",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LI",
  "LK",
  "LS",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "MN",
  "MP",
  "MQ",
  "MS",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NO",
  "NP",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PM",
  "PN",
  "PR",
  "PS",
  "PW",
  "PY",
  "RE",
  "RS",
  "SA",
  "SC",
  "SH",
  "SJ",
  "SM",
  "SN",
  "SV",
  "SX",
  "SZ",
  "TC",
  "TF",
  "TH",
  "TJ",
  "TM",
  "TN",
  "TR",
  "TT",
  "TW",
  "TZ",
  "UA",
  "UM",
  "UY",
  "UZ",
  "VA",
  "VG",
  "VI",
  "VN",
  "WF",
  "YT",
  "ZA",
  "ZM",
]);

// the API takes a blank second street line but not null, which the BIC
// lookup's branch address can carry
const withBlankStreet2 = <T extends { street_address_2?: string | null }>(
  address: T,
): T => ({ ...address, street_address_2: address.street_address_2 ?? "" });

export const transformSwiftFormFieldsToApiFields = ({
  bic,
  account_identifier,
  metadata,
  address,
  branch_address,
  correspondent_bank_bic,
  isCorrespondentBankEnabled,
}: SwiftDestinationFields & {
  isCorrespondentBankEnabled?: boolean;
}) => {
  // Strip empty metadata values (preserve iban boolean flag)
  const cleanedMetadata = Object.fromEntries(
    Object.entries(metadata).filter(
      ([key, value]) => key === "iban" || !!value,
    ),
  );
  return {
    bic: bic || null,
    account_identifier,
    metadata: cleanedMetadata,
    address: withBlankStreet2(address),
    branch_address: branch_address ? withBlankStreet2(branch_address) : null,
    correspondent_bank_bic:
      isCorrespondentBankEnabled && correspondent_bank_bic
        ? correspondent_bank_bic
        : null,
  };
};

type Alpha2CountryCodes = {
  code: string;
  name: string;
};

// TODO: may want to pull supported countries from backend eventually
export const SWIFT_DESTINATION_COUNTRIES: Alpha2CountryCodes[] = [
  { code: "AD", name: "Andorra" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AI", name: "Anguilla" },
  { code: "AL", name: "Albania" },
  { code: "AM", name: "Armenia" },
  { code: "AO", name: "Angola" },
  { code: "AQ", name: "Antarctica" },
  { code: "AR", name: "Argentina" },
  { code: "AS", name: "American Samoa" },
  { code: "AT", name: "Austria" },
  { code: "AU", name: "Australia" },
  { code: "AW", name: "Aruba" },
  { code: "AX", name: "Åland Islands" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BB", name: "Barbados" },
  { code: "BD", name: "Bangladesh" },
  { code: "BE", name: "Belgium" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BG", name: "Bulgaria" },
  { code: "BH", name: "Bahrain" },
  { code: "BI", name: "Burundi" },
  { code: "BJ", name: "Benin" },
  { code: "BL", name: "Saint Barthélemy" },
  { code: "BM", name: "Bermuda" },
  { code: "BN", name: "Brunei Darussalam" },
  { code: "BO", name: "Bolivia" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BR", name: "Brazil" },
  { code: "BS", name: "Bahamas" },
  { code: "BT", name: "Bhutan" },
  { code: "BV", name: "Bouvet Island" },
  { code: "BW", name: "Botswana" },
  { code: "BZ", name: "Belize" },
  { code: "CA", name: "Canada" },
  { code: "CC", name: "Cocos (Keeling) Islands" },
  { code: "CF", name: "Central African Republic" },
  { code: "CH", name: "Switzerland" },
  { code: "CI", name: "Côte d'Ivoire" },
  { code: "CK", name: "Cook Islands" },
  { code: "CL", name: "Chile" },
  { code: "CM", name: "Cameroon" },
  { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" },
  { code: "CR", name: "Costa Rica" },
  { code: "CV", name: "Cabo Verde" },
  { code: "CW", name: "Curaçao" },
  { code: "CX", name: "Christmas Island" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" },
  { code: "DE", name: "Germany" },
  { code: "DJ", name: "Djibouti" },
  { code: "DK", name: "Denmark" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "DZ", name: "Algeria" },
  { code: "EC", name: "Ecuador" },
  { code: "EE", name: "Estonia" },
  { code: "EG", name: "Egypt" },
  { code: "EH", name: "Western Sahara" },
  { code: "ER", name: "Eritrea" },
  { code: "ES", name: "Spain" },
  { code: "ET", name: "Ethiopia" },
  { code: "FI", name: "Finland" },
  { code: "FJ", name: "Fiji" },
  { code: "FK", name: "Falkland Islands (Malvinas)" },
  { code: "FM", name: "Micronesia, Federated States of" },
  { code: "FO", name: "Faroe Islands" },
  { code: "FR", name: "France" },
  { code: "GA", name: "Gabon" },
  { code: "GB", name: "United Kingdom" },
  { code: "GD", name: "Grenada" },
  { code: "GE", name: "Georgia" },
  { code: "GF", name: "French Guiana" },
  { code: "GG", name: "Guernsey" },
  { code: "GH", name: "Ghana" },
  { code: "GI", name: "Gibraltar" },
  { code: "GL", name: "Greenland" },
  { code: "GM", name: "Gambia" },
  { code: "GN", name: "Guinea" },
  { code: "GP", name: "Guadeloupe" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "GR", name: "Greece" },
  { code: "GS", name: "South Georgia and the South Sandwich Islands" },
  { code: "GT", name: "Guatemala" },
  { code: "GU", name: "Guam" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HK", name: "Hong Kong" },
  { code: "HM", name: "Heard Island and McDonald Islands" },
  { code: "HN", name: "Honduras" },
  { code: "HR", name: "Croatia" },
  { code: "HU", name: "Hungary" },
  { code: "ID", name: "Indonesia" },
  { code: "IE", name: "Ireland" },
  { code: "IL", name: "Israel" },
  { code: "IM", name: "Isle of Man" },
  { code: "IN", name: "India" },
  { code: "IS", name: "Iceland" },
  { code: "IT", name: "Italy" },
  { code: "JE", name: "Jersey" },
  { code: "JM", name: "Jamaica" },
  { code: "JO", name: "Jordan" },
  { code: "JP", name: "Japan" },
  { code: "KE", name: "Kenya" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "KH", name: "Cambodia" },
  { code: "KI", name: "Kiribati" },
  { code: "KM", name: "Comoros" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "KR", name: "South Korea" },
  { code: "KW", name: "Kuwait" },
  { code: "KY", name: "Cayman Islands" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "LA", name: "Laos" },
  { code: "LB", name: "Lebanon" },
  { code: "LC", name: "Saint Lucia" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LK", name: "Sri Lanka" },
  { code: "LR", name: "Liberia" },
  { code: "LS", name: "Lesotho" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "LV", name: "Latvia" },
  { code: "MA", name: "Morocco" },
  { code: "MC", name: "Monaco" },
  { code: "MD", name: "Moldova" },
  { code: "ME", name: "Montenegro" },
  { code: "MF", name: "Saint Martin (French part)" },
  { code: "MG", name: "Madagascar" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MK", name: "North Macedonia" },
  { code: "ML", name: "Mali" },
  { code: "MN", name: "Mongolia" },
  { code: "MO", name: "Macao" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "MQ", name: "Martinique" },
  { code: "MR", name: "Mauritania" },
  { code: "MS", name: "Montserrat" },
  { code: "MT", name: "Malta" },
  { code: "MU", name: "Mauritius" },
  { code: "MV", name: "Maldives" },
  { code: "MW", name: "Malawi" },
  { code: "MX", name: "Mexico" },
  { code: "MY", name: "Malaysia" },
  { code: "MZ", name: "Mozambique" },
  { code: "NA", name: "Namibia" },
  { code: "NC", name: "New Caledonia" },
  { code: "NE", name: "Niger" },
  { code: "NF", name: "Norfolk Island" },
  { code: "NG", name: "Nigeria" },
  { code: "NI", name: "Nicaragua" },
  { code: "NL", name: "Netherlands" },
  { code: "NO", name: "Norway" },
  { code: "NP", name: "Nepal" },
  { code: "NR", name: "Nauru" },
  { code: "NU", name: "Niue" },
  { code: "NZ", name: "New Zealand" },
  { code: "OM", name: "Oman" },
  { code: "PA", name: "Panama" },
  { code: "PE", name: "Peru" },
  { code: "PF", name: "French Polynesia" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PH", name: "Philippines" },
  { code: "PK", name: "Pakistan" },
  { code: "PL", name: "Poland" },
  { code: "PM", name: "Saint Pierre and Miquelon" },
  { code: "PN", name: "Pitcairn" },
  { code: "PR", name: "Puerto Rico" },
  { code: "PS", name: "Palestine, State of" },
  { code: "PT", name: "Portugal" },
  { code: "PW", name: "Palau" },
  { code: "PY", name: "Paraguay" },
  { code: "QA", name: "Qatar" },
  { code: "RE", name: "Réunion" },
  { code: "RO", name: "Romania" },
  { code: "RS", name: "Serbia" },
  { code: "RW", name: "Rwanda" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SC", name: "Seychelles" },
  { code: "SE", name: "Sweden" },
  { code: "SG", name: "Singapore" },
  { code: "SH", name: "Saint Helena, Ascension and Tristan da Cunha" },
  { code: "SI", name: "Slovenia" },
  { code: "SJ", name: "Svalbard and Jan Mayen" },
  { code: "SK", name: "Slovakia" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SM", name: "San Marino" },
  { code: "SN", name: "Senegal" },
  { code: "SR", name: "Suriname" },
  { code: "ST", name: "Sao Tome and Principe" },
  { code: "SV", name: "El Salvador" },
  { code: "SX", name: "Sint Maarten (Dutch part)" },
  { code: "SZ", name: "Eswatini" },
  { code: "TC", name: "Turks and Caicos Islands" },
  { code: "TD", name: "Chad" },
  { code: "TF", name: "French Southern Territories" },
  { code: "TG", name: "Togo" },
  { code: "TH", name: "Thailand" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TK", name: "Tokelau" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TN", name: "Tunisia" },
  { code: "TO", name: "Tonga" },
  { code: "TR", name: "Türkiye" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "TV", name: "Tuvalu" },
  { code: "TW", name: "Taiwan" },
  { code: "TZ", name: "Tanzania" },
  { code: "UA", name: "Ukraine" },
  { code: "UG", name: "Uganda" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "US", name: "United States" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "VA", name: "Holy See (Vatican City State)" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "VG", name: "Virgin Islands, British" },
  { code: "VI", name: "Virgin Islands, U.S." },
  { code: "VN", name: "Vietnam" },
  { code: "VU", name: "Vanuatu" },
  { code: "WF", name: "Wallis and Futuna" },
  { code: "WS", name: "Samoa" },
  { code: "YE", name: "Yemen" },
  { code: "YT", name: "Mayotte" },
  { code: "ZA", name: "South Africa" },
  { code: "ZM", name: "Zambia" },
];

export const PHONE_COUNTRIES: { [key: string]: Alpha2CountryCodes } = {
  "1": { code: "US", name: "United States" },
  "20": { code: "EG", name: "Egypt" },
  "27": { code: "ZA", name: "South Africa" },
  "30": { code: "GR", name: "Greece" },
  "31": { code: "NL", name: "Netherlands" },
  "32": { code: "BE", name: "Belgium" },
  "33": { code: "FR", name: "France" },
  "34": { code: "ES", name: "Spain" },
  "36": { code: "HU", name: "Hungary" },
  "39": { code: "VA", name: "Vatican City" },
  "40": { code: "RO", name: "Romania" },
  "41": { code: "CH", name: "Switzerland" },
  "43": { code: "AT", name: "Austria" },
  "44": { code: "JE", name: "Jersey" },
  "45": { code: "DK", name: "Denmark" },
  "46": { code: "SE", name: "Sweden" },
  "47": { code: "SJ", name: "Svalbard and Jan Mayen" },
  "48": { code: "PL", name: "Poland" },
  "49": { code: "DE", name: "Germany" },
  "51": { code: "PE", name: "Peru" },
  "52": { code: "MX", name: "Mexico" },
  "54": { code: "AR", name: "Argentina" },
  "55": { code: "BR", name: "Brazil" },
  "56": { code: "CL", name: "Chile" },
  "57": { code: "CO", name: "Colombia" },
  "60": { code: "MY", name: "Malaysia" },
  "61": { code: "CX", name: "Christmas Island" },
  "62": { code: "ID", name: "Indonesia" },
  "63": { code: "PH", name: "Philippines" },
  "64": { code: "NZ", name: "New Zealand" },
  "65": { code: "SG", name: "Singapore" },
  "81": { code: "JP", name: "Japan" },
  "82": { code: "KR", name: "South Korea" },
  "84": { code: "VN", name: "Vietnam" },
  "86": { code: "CN", name: "China" },
  "90": { code: "TR", name: "Turkey" },
  "91": { code: "IN", name: "India" },
  "92": { code: "PK", name: "Pakistan" },
  "94": { code: "LK", name: "Sri Lanka" },
  "212": { code: "MA", name: "Morocco" },
  "216": { code: "TN", name: "Tunisia" },
  "220": { code: "GM", name: "Gambia" },
  "221": { code: "SN", name: "Senegal" },
  "222": { code: "MR", name: "Mauritania" },
  "223": { code: "ML", name: "Mali" },
  "224": { code: "GN", name: "Guinea" },
  "225": { code: "CI", name: "Côte d'Ivoire" },
  "226": { code: "BF", name: "Burkina Faso" },
  "227": { code: "NE", name: "Niger" },
  "228": { code: "TG", name: "Togo" },
  "229": { code: "BJ", name: "Benin" },
  "230": { code: "MU", name: "Mauritius" },
  "231": { code: "LR", name: "Liberia" },
  "232": { code: "SL", name: "Sierra Leone" },
  "233": { code: "GH", name: "Ghana" },
  "234": { code: "NG", name: "Nigeria" },
  "235": { code: "TD", name: "Chad" },
  "236": { code: "CF", name: "Central African Republic" },
  "237": { code: "CM", name: "Cameroon" },
  "238": { code: "CV", name: "Cape Verde" },
  "239": { code: "ST", name: "São Tomé and Príncipe" },
  "240": { code: "GQ", name: "Equatorial Guinea" },
  "241": { code: "GA", name: "Gabon" },
  "242": { code: "CG", name: "Republic of the Congo" },
  "244": { code: "AO", name: "Angola" },
  "245": { code: "GW", name: "Guinea-Bissau" },
  "246": { code: "IO", name: "British Indian Ocean Territory" },
  "248": { code: "SC", name: "Seychelles" },
  "250": { code: "RW", name: "Rwanda" },
  "251": { code: "ET", name: "Ethiopia" },
  "253": { code: "DJ", name: "Djibouti" },
  "254": { code: "KE", name: "Kenya" },
  "255": { code: "TZ", name: "Tanzania" },
  "256": { code: "UG", name: "Uganda" },
  "257": { code: "BI", name: "Burundi" },
  "258": { code: "MZ", name: "Mozambique" },
  "260": { code: "ZM", name: "Zambia" },
  "261": { code: "MG", name: "Madagascar" },
  "262": { code: "YT", name: "Mayotte" },
  "264": { code: "NA", name: "Namibia" },
  "265": { code: "MW", name: "Malawi" },
  "266": { code: "LS", name: "Lesotho" },
  "267": { code: "BW", name: "Botswana" },
  "268": { code: "SZ", name: "Eswatini" },
  "269": { code: "KM", name: "Comoros" },
  "291": { code: "ER", name: "Eritrea" },
  "297": { code: "AW", name: "Aruba" },
  "298": { code: "FO", name: "Faroe Islands" },
  "299": { code: "GL", name: "Greenland" },
  "350": { code: "GI", name: "Gibraltar" },
  "351": { code: "PT", name: "Portugal" },
  "352": { code: "LU", name: "Luxembourg" },
  "353": { code: "IE", name: "Ireland" },
  "354": { code: "IS", name: "Iceland" },
  "355": { code: "AL", name: "Albania" },
  "356": { code: "MT", name: "Malta" },
  "357": { code: "CY", name: "Cyprus" },
  "358": { code: "FI", name: "Finland" },
  "359": { code: "BG", name: "Bulgaria" },
  "370": { code: "LT", name: "Lithuania" },
  "371": { code: "LV", name: "Latvia" },
  "372": { code: "EE", name: "Estonia" },
  "373": { code: "MD", name: "Moldova" },
  "374": { code: "AM", name: "Armenia" },
  "376": { code: "AD", name: "Andorra" },
  "377": { code: "MC", name: "Monaco" },
  "378": { code: "SM", name: "San Marino" },
  "381": { code: "RS", name: "Serbia" },
  "382": { code: "ME", name: "Montenegro" },
  "383": { code: "XK", name: "Kosovo" },
  "385": { code: "HR", name: "Croatia" },
  "386": { code: "SI", name: "Slovenia" },
  "387": { code: "BA", name: "Bosnia and Herzegovina" },
  "389": { code: "MK", name: "North Macedonia" },
  "420": { code: "CZ", name: "Czech Republic" },
  "421": { code: "SK", name: "Slovakia" },
  "423": { code: "LI", name: "Liechtenstein" },
  "500": { code: "FK", name: "Falkland Islands" },
  "501": { code: "BZ", name: "Belize" },
  "502": { code: "GT", name: "Guatemala" },
  "503": { code: "SV", name: "El Salvador" },
  "504": { code: "HN", name: "Honduras" },
  "506": { code: "CR", name: "Costa Rica" },
  "507": { code: "PA", name: "Panama" },
  "508": { code: "PM", name: "Saint Pierre and Miquelon" },
  "590": { code: "MF", name: "Saint Martin" },
  "591": { code: "BO", name: "Bolivia" },
  "592": { code: "GY", name: "Guyana" },
  "593": { code: "EC", name: "Ecuador" },
  "594": { code: "GF", name: "French Guiana" },
  "595": { code: "PY", name: "Paraguay" },
  "596": { code: "MQ", name: "Martinique" },
  "597": { code: "SR", name: "Suriname" },
  "598": { code: "UY", name: "Uruguay" },
  "599": { code: "CW", name: "Curaçao" },
  "670": { code: "TL", name: "Timor-Leste" },
  "672": { code: "NF", name: "Norfolk Island" },
  "673": { code: "BN", name: "Brunei" },
  "674": { code: "NR", name: "Nauru" },
  "675": { code: "PG", name: "Papua New Guinea" },
  "676": { code: "TO", name: "Tonga" },
  "677": { code: "SB", name: "Solomon Islands" },
  "678": { code: "VU", name: "Vanuatu" },
  "679": { code: "FJ", name: "Fiji" },
  "680": { code: "PW", name: "Palau" },
  "681": { code: "WF", name: "Wallis and Futuna" },
  "682": { code: "CK", name: "Cook Islands" },
  "683": { code: "NU", name: "Niue" },
  "685": { code: "WS", name: "Samoa" },
  "686": { code: "KI", name: "Kiribati" },
  "687": { code: "NC", name: "New Caledonia" },
  "688": { code: "TV", name: "Tuvalu" },
  "689": { code: "PF", name: "French Polynesia" },
  "690": { code: "TK", name: "Tokelau" },
  "691": { code: "FM", name: "Micronesia" },
  "692": { code: "MH", name: "Marshall Islands" },
  "852": { code: "HK", name: "Hong Kong" },
  "853": { code: "MO", name: "Macao" },
  "856": { code: "LA", name: "Laos" },
  "880": { code: "BD", name: "Bangladesh" },
  "886": { code: "TW", name: "Taiwan" },
  "960": { code: "MV", name: "Maldives" },
  "968": { code: "OM", name: "Oman" },
  "972": { code: "IL", name: "Israel" },
  "973": { code: "BH", name: "Bahrain" },
  "974": { code: "QA", name: "Qatar" },
  "975": { code: "BT", name: "Bhutan" },
  "976": { code: "MN", name: "Mongolia" },
  "977": { code: "NP", name: "Nepal" },
  "992": { code: "TJ", name: "Tajikistan" },
  "993": { code: "TM", name: "Turkmenistan" },
  "994": { code: "AZ", name: "Azerbaijan" },
  "995": { code: "GE", name: "Georgia" },
  "996": { code: "KG", name: "Kyrgyzstan" },
  "998": { code: "UZ", name: "Uzbekistan" },
};

export const SUPPORTED_COUNTRY_CODES = Object.entries(PHONE_COUNTRIES).map(
  ([phoneCode, { code, name }]) => ({
    code,
    name,
    label: `${name} (+${phoneCode})`,
    countryCallingCode: `+${phoneCode}`,
  }),
);

function validateSwiftDestinationFields(
  data: {
    bic?: string | null;
    account_identifier?: string;
    metadata?: {
      iban?: string | boolean;
      routing_number?: string;
      phone?: string;
      itin?: string;
      tin?: string;
      contact?: string;
      [key: string]: string | boolean | undefined;
    };
    additionalFieldsToShow?: Record<string, boolean>;
    renameFields?: Record<string, string>;
    branch_address?: {
      country_code?: string;
      street_address?: string;
      city?: string;
      region_code?: string;
      postal_code?: string;
    } | null;
  },
  ctx: z.RefinementCtx,
  pathPrefix: string[] = [],
) {
  Object.entries(data.additionalFieldsToShow || {}).forEach(([key, value]) => {
    if (value) {
      if (key === "branch_address") {
        const requiredBranchFields = [
          "country_code",
          "street_address",
          "city",
          "region_code",
          "postal_code",
        ] as const;

        requiredBranchFields.forEach((field) => {
          if (!data.branch_address?.[field]?.trim()) {
            ctx.addIssue({
              path: [...pathPrefix, "branch_address", field],
              code: z.ZodIssueCode.custom,
              message: "Required",
            });
          }
        });
      } else if (key === "phone") {
        const phoneValue = data.metadata?.[key];
        const { isValid, nationalNumber } = parseInternationalPhoneNumber(
          phoneValue || "",
        );

        if (!nationalNumber?.trim()) {
          ctx.addIssue({
            path: [...pathPrefix, "metadata", key],
            code: z.ZodIssueCode.custom,
            message: "Required",
          });
        } else if (!isValid) {
          ctx.addIssue({
            path: [...pathPrefix, "metadata", key],
            code: z.ZodIssueCode.custom,
            message: "Phone number is invalid",
          });
        }
      } else if (key === "iban") {
        // iban in metadata is a boolean flag set by the server, not a user-entered value.
        // The actual IBAN value lives in account_identifier, validated by validateRequiredFields.
      } else if (key === "bank_id") {
        // bank_id is optional — no validation needed
      } else if (
        !data.metadata?.[key as keyof typeof data.metadata]?.toString()?.trim()
      ) {
        ctx.addIssue({
          path: [...pathPrefix, "metadata", key],
          code: z.ZodIssueCode.custom,
          message: "Required",
        });
      }
    }
  });
}

function validateRequiredFields(
  data: {
    bic?: string | null;
    account_identifier?: string;
    address?: {
      country_code?: string;
      street_address?: string;
      city?: string;
      region_code?: string;
      postal_code?: string;
    };
  },
  ctx: z.RefinementCtx,
  pathPrefix: string[] = [],
  skipBicRequired = false,
) {
  if (!skipBicRequired && !data.bic?.trim()) {
    ctx.addIssue({
      path: [...pathPrefix, "bic"],
      code: z.ZodIssueCode.custom,
      message: "Required",
    });
  }
  if (!data.account_identifier?.trim()) {
    ctx.addIssue({
      path: [...pathPrefix, "account_identifier"],
      code: z.ZodIssueCode.custom,
      message: "Required",
    });
  }
  const requiredAddressFields = [
    "country_code",
    "street_address",
    "city",
    "region_code",
    "postal_code",
  ] as const;
  requiredAddressFields.forEach((field) => {
    if (!data.address?.[field]?.trim()) {
      ctx.addIssue({
        path: [...pathPrefix, "address", field],
        code: z.ZodIssueCode.custom,
        message: "Required",
      });
    }
  });
}

const addressSchema = z
  .object({
    country_code: z.string(),
    street_address: z.string(),
    street_address_2: z.string().nullish(),
    city: z.string(),
    region_code: z.string(),
    postal_code: z.string(),
  })
  .transform((address) => {
    if (POSTAL_CODE_NOT_APPLICABLE_COUNTRIES.has(address.country_code)) {
      return { ...address, postal_code: DEFAULT_POSTAL_CODE };
    }
    if (
      POSTAL_CODE_OPTIONAL_COUNTRIES.has(address.country_code) &&
      !address.postal_code.trim()
    ) {
      return { ...address, postal_code: DEFAULT_POSTAL_CODE };
    }
    return address;
  });

const swiftDestinationFieldsShape = {
  bic: z.string().nullish(),
  account_identifier: z.string(),
  metadata: z
    .object({
      iban: z.union([z.string(), z.boolean()]).optional(),
      routing_number: z.string().optional(),
      phone: z.string().optional(),
      itin: z.string().optional(),
      tin: z.string().optional(),
      contact: z.string().optional(),
      bank_id: z.string().optional(),
      bank_name: z.string().optional(),
    })
    .catchall(z.string().optional())
    .default({}),
  additionalFieldsToShow: z.record(z.boolean()).default({}),
  renameFields: z.record(z.string()).default({}),
  address: addressSchema,
  branch_address: addressSchema.nullish(),
};

export function createSchema() {
  return z
    .object({
      ...swiftDestinationFieldsShape,
      isCorrespondentBankEnabled: z.boolean().default(false),
      correspondent_bank_bic: z.string().nullish(),
    })
    .superRefine((data, ctx) => {
      if (data.isCorrespondentBankEnabled) {
        if (!data.correspondent_bank_bic?.trim()) {
          ctx.addIssue({
            path: ["correspondent_bank_bic"],
            code: z.ZodIssueCode.custom,
            message: "Required",
          });
        }
        // bank_name is required when correspondent bank is enabled
        if (!data.metadata?.bank_name?.trim()) {
          ctx.addIssue({
            path: ["metadata", "bank_name"],
            code: z.ZodIssueCode.custom,
            message: "Required",
          });
        }
        // branch_address fields are required when correspondent bank is enabled
        const requiredBranchFields = [
          "country_code",
          "street_address",
          "city",
          "region_code",
          "postal_code",
        ] as const;
        requiredBranchFields.forEach((field) => {
          if (!data.branch_address?.[field]?.trim()) {
            ctx.addIssue({
              path: ["branch_address", field],
              code: z.ZodIssueCode.custom,
              message: "Required",
            });
          }
        });
      }
      // Primary fields are always required (bic is optional when correspondent bank is enabled)
      validateRequiredFields(data, ctx, [], data.isCorrespondentBankEnabled);
      validateSwiftDestinationFields(data, ctx);
    });
}

export function createDefaultValues() {
  return {
    bic: "",
    account_identifier: "",
    metadata: {},
    additionalFieldsToShow: {},
    renameFields: {},
    address: {
      country_code: "",
      street_address: "",
      street_address_2: "",
      city: "",
      region_code: "",
      postal_code: "",
    },
    isCorrespondentBankEnabled: false,
    correspondent_bank_bic: "",
  };
}

// define form schema and validations
export const useSwiftDestinationForm = () => {
  const schema = createSchema();
  return useForm<SwiftDestinationFormType>({
    defaultValues: createDefaultValues(),
    resolver: zodResolver(schema),
  });
};
